import { CreateEntityResponseModel, SuccessResponseModel } from '@models';
import {
  ComboGoalGraphModel,
  GoalCategoryCid,
  GoalEarningsModel,
  GoalGetFiltersModel,
  GoalModel,
  GoalPriceDetailsModel,
  GoalPriceModel,
  GoalPricePatchModel,
  GoalVisibilityVectorModel,
  MentorEarningsModel
} from '@models/goals';
import { VersionedEntityType } from '@models/versions';
import * as Sentry from '@sentry/nextjs';
import { useCallback, useState } from 'react';
import { useImprvApi } from './useImprvApi';

export const useGoalsService = () => {
  const [isPendingGetComboGoalGraph, setIsPendingGetComboGoalGraph] = useState(false);
  const [isPendingGetGoals, setIsPendingGetGoals] = useState(false);
  const [isPendingGetGoal, setIsPendingGetGoal] = useState(false);
  const [isPendingUpdateGoal, setIsPendingUpdateGoal] = useState(false);
  const [isPendingGetGoalVisibilityVector, setIsPendingGetGoalVisibilityVector] = useState(false);
  const [isPendingUpdateGoalVisibilityVector, setIsPendingUpdateGoalVisibilityVector] =
    useState(false);
  const [isPendingGetGoalLandingPage, setIsPendingGetGoalLandingPage] = useState(false);
  const imprvApi = useImprvApi();
  /**
   * Fetches a combo goal graph for a specific goal
   * @param goalEntityId The entity ID of the goal to fetch the graph for
   * @returns Promise resolving to the ComboGoalGraphModel
   */
  const getComboGoalGraph = useCallback(
    async (goalEntityId: string): Promise<ComboGoalGraphModel> => {
      try {
        setIsPendingGetComboGoalGraph(true);
        return await imprvApi.getComboGoalGraph(goalEntityId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingGetComboGoalGraph(false);
      }
    },
    [imprvApi]
  );

  /**
   * Fetches goals based on provided parameters
   * @param params Optional parameters for filtering and searching goals
   * @returns Promise resolving to an array of GoalModel
   */
  const getGoals = useCallback(
    async (params: {
      query?: string;
      semanticQuery?: string;
      filters?: GoalGetFiltersModel;
      comboGoalEntityId?: string;
      childGoalEntityId?: string;
      goalEntityIds?: string[];
      excludeSubscribed?: boolean;
      canEnroll?: boolean;
      categoryCid?: GoalCategoryCid;
      maxVersionType?: VersionedEntityType;
      withRecommendations?: boolean;
    }): Promise<{ goals: GoalModel[] }> => {
      try {
        setIsPendingGetGoals(true);
        return await imprvApi.getGoals(params);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingGetGoals(false);
      }
    },
    [imprvApi]
  );

  /**
   * Fetches a specific goal by its entity ID
   * @param goalEntityId The entity ID of the goal to fetch
   * @returns Promise resolving to the GoalModel
   */
  const getGoal = useCallback(
    async (goalEntityId: string, maxVersionType?: VersionedEntityType): Promise<GoalModel> => {
      try {
        setIsPendingGetGoal(true);
        return await imprvApi.getGoal(
          goalEntityId,
          maxVersionType || VersionedEntityType.PUBLISHED
        );
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingGetGoal(false);
      }
    },
    [imprvApi]
  );

  const createGoalPrice = useCallback(
    async (
      goalEntityId: string,
      goalPrice: GoalPricePatchModel
    ): Promise<CreateEntityResponseModel> => {
      try {
        return await imprvApi.createGoalPrice(goalEntityId, goalPrice);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const getGoalPrices = useCallback(
    async (goalEntityId: string): Promise<GoalPriceModel[]> => {
      try {
        return await imprvApi.getGoalPrices(goalEntityId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const getGoalPrice = useCallback(
    async (
      goalEntityId: string,
      goalPriceEntityId: string,
      maxVersionType?: VersionedEntityType
    ): Promise<GoalPriceModel> => {
      try {
        return await imprvApi.getGoalPrice(
          goalEntityId,
          goalPriceEntityId,
          maxVersionType || VersionedEntityType.PUBLISHED
        );
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const updateGoalPrice = useCallback(
    async (
      goalEntityId: string,
      goalPriceEntityId: string,
      goalPrice: GoalPricePatchModel
    ): Promise<SuccessResponseModel> => {
      try {
        return await imprvApi.updateGoalPrice(goalEntityId, goalPriceEntityId, goalPrice);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const publishGoalPrice = useCallback(
    async (
      goalEntityId: string,
      goalPriceEntityId: string,
      goalPrice: GoalPricePatchModel
    ): Promise<SuccessResponseModel> => {
      try {
        return await imprvApi.publishGoalPrice(goalEntityId, goalPriceEntityId, goalPrice);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const updateGoal = useCallback(
    async (
      goalEntityId: string,
      patch: Partial<Pick<GoalModel, 'privacy'>>
    ): Promise<SuccessResponseModel> => {
      try {
        setIsPendingUpdateGoal(true);
        return await imprvApi.updateGoal(goalEntityId, patch);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingUpdateGoal(false);
      }
    },
    [imprvApi]
  );

  const getGoalVisibilityVector = useCallback(
    async (goalEntityId: string): Promise<GoalVisibilityVectorModel> => {
      try {
        setIsPendingGetGoalVisibilityVector(true);
        return await imprvApi.getGoalVisibilityVector(goalEntityId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingGetGoalVisibilityVector(false);
      }
    },
    [imprvApi]
  );

  const updateGoalVisibilityVector = useCallback(
    async (
      goalEntityId: string,
      vector: GoalVisibilityVectorModel
    ): Promise<SuccessResponseModel> => {
      try {
        setIsPendingUpdateGoalVisibilityVector(true);
        return await imprvApi.updateGoalVisibilityVector(goalEntityId, vector);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingUpdateGoalVisibilityVector(false);
      }
    },
    [imprvApi]
  );

  const getGoalLandingPage = useCallback(
    async (goalEntityId: string, maxVersionType?: VersionedEntityType): Promise<GoalModel> => {
      try {
        setIsPendingGetGoalLandingPage(true);
        return await imprvApi.getGoalLandingPage(goalEntityId, maxVersionType);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingGetGoalLandingPage(false);
      }
    },
    [imprvApi]
  );

  const calculateGoalPriceDetails = useCallback(
    async (pricing: GoalPriceDetailsModel): Promise<GoalPriceDetailsModel> => {
      try {
        return await imprvApi.calculateGoalPriceDetails(pricing);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const getGoalEarnings = useCallback(
    async (goalEntityId: string): Promise<GoalEarningsModel> => {
      try {
        return await imprvApi.getGoalEarnings(goalEntityId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const getAccountMentorEarnings = useCallback(async (): Promise<MentorEarningsModel> => {
    try {
      return await imprvApi.getAccountMentorEarnings();
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }, [imprvApi]);

  return {
    getComboGoalGraph,
    isPendingGetComboGoalGraph,
    getGoals,
    isPendingGetGoals,
    getGoal,
    isPendingGetGoal,
    createGoalPrice,
    getGoalPrices,
    getGoalPrice,
    updateGoalPrice,
    publishGoalPrice,
    calculateGoalPriceDetails,
    updateGoal,
    isPendingUpdateGoal,
    getGoalEarnings,
    getAccountMentorEarnings,
    getGoalVisibilityVector,
    isPendingGetGoalVisibilityVector,
    updateGoalVisibilityVector,
    isPendingUpdateGoalVisibilityVector,
    getGoalLandingPage,
    isPendingGetGoalLandingPage
  };
};
